import api from './api';

export interface Post {
  id: string;
  client_id: string;
  user_id: string;
  title: string;
  content: string;
  media_files: string[];
  status: 'draft' | 'scheduled' | 'published';
  scheduled_at?: string;
  published_at?: string;
  created_at: string;
  updated_at: string;
  platforms?: string[];
}

export interface PostGroup {
  id: string;
  client_id: string;
  user_id: string;
  title: string;
  status: 'draft' | 'scheduled' | 'published';
  scheduled_at?: string;
  created_at: string;
  updated_at: string;
  posts: Post[];
}

export interface CreatePostData {
  title: string;
  content: string;
  media_files?: string[];
  scheduled_at?: string;
  platforms?: string[];
}

export interface CreatePostGroupData {
  title: string;
  status: 'draft' | 'scheduled' | 'published';
  scheduled_at?: string;
  posts: CreatePostData[];
}

export interface UpdatePostData {
  title?: string;
  content?: string;
  media_files?: string[];
  scheduled_at?: string;
  status?: 'draft' | 'scheduled' | 'published';
  platforms?: string[];
}

export interface UpdatePostGroupData {
  title?: string;
  status?: 'draft' | 'scheduled' | 'published';
  scheduled_at?: string;
}

export interface PostListResponse {
  posts: Post[];
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface PostGroupListResponse {
  post_groups: PostGroup[];
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

class PostService {
  async getPosts(page = 1, perPage = 20, status?: string, search?: string): Promise<PostListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (status) {
      params.append('status', status);
    }

    if (search) {
      params.append('search', search);
    }

    const response = await api.get(`/posts/?${params}`);
    return response.data;
  }

  async getPost(postId: string): Promise<Post> {
    const response = await api.get(`/posts/${postId}`);
    return response.data;
  }

  async createPost(data: CreatePostData): Promise<Post> {
    const response = await api.post('/posts/', data);
    return response.data;
  }

  async updatePost(postId: string, data: UpdatePostData): Promise<Post> {
    // Debug logging to see what's being sent to the API
    console.log('PostService: Sending update request:', {
      postId,
      data,
      contentLength: data.content?.length || 0,
      hasContent: !!data.content
    });
    
    const response = await api.put(`/posts/${postId}`, data);
    
    console.log('PostService: Update response received:', {
      status: response.status,
      data: response.data,
      updatedContent: response.data?.content,
      updatedContentLength: response.data?.content?.length || 0
    });
    
    return response.data;
  }

  async deletePost(postId: string): Promise<void> {
    await api.delete(`/posts/${postId}`);
  }

  async publishPost(postId: string, imageUrls: string[], caption: string, platform?: string, contentType?: string): Promise<Post> {
    // Use the original working approach - direct platform publishing
    const targetPlatform = platform || 'instagram';
    const targetContentType = contentType || 'feed';
    
    if (targetPlatform === 'youtube') {
      // YouTube requires video files, not image files
      const videoUrl = imageUrls[0]; // For YouTube, the first "image" URL should be a video URL
      console.log('📺 Publishing to YouTube:', {
        postId,
        videoUrl,
        title: caption.split('\n')[0] || 'Untitled Video',
        description: caption
      });
      
      const response = await api.post(`/social/post/youtube`, {
        post_id: postId,
        video_url: videoUrl,
        title: caption.split('\n')[0] || 'Untitled Video', // Use first line as title
        description: caption,
        tags: [],
        privacy_status: 'public', // Changed to 'public' so videos appear on channel
        category_id: '22'
      });
      
      console.log('📺 YouTube publish response:', response.data);
      return response.data;
    } else if (targetPlatform === 'facebook') {
      // Facebook supports both images and videos
      const isVideo = imageUrls.some(url => url.includes('.mp4') || url.includes('.mov') || url.includes('.avi'));
      
      console.log('📘 Publishing to Facebook:', {
        postId,
        isVideo,
        mediaUrl: imageUrls[0],
        caption
      });
      
      const response = await api.post(`/social/post/facebook`, {
        post_id: postId,
        image_urls: isVideo ? [] : imageUrls,
        video_url: isVideo ? imageUrls[0] : null,
        caption: caption,
        content_type: targetContentType
      });
      
      console.log('📘 Facebook publish response:', response.data);
      return response.data;
    } else if (targetPlatform === 'twitter') {
      // Twitter supports text and up to 4 images
      console.log('🐦 Publishing to Twitter:', {
        postId,
        message: caption.substring(0, 280), // Twitter character limit
        mediaUrls: imageUrls.slice(0, 4) // Max 4 images
      });
      
      const response = await api.post(`/social/post/twitter`, {
        post_id: postId,
        message: caption.substring(0, 280), // Enforce 280 character limit
        media_urls: imageUrls.slice(0, 4) // Max 4 images on Twitter
      });
      
      console.log('🐦 Twitter publish response:', response.data);
      return response.data;
    } else {
      // For other platforms (Instagram, etc.)
      const response = await api.post(`/social/post/${targetPlatform}`, { 
        post_id: postId, 
        image_urls: imageUrls, 
        caption: caption,
        content_type: targetContentType
      });
      return response.data;
    }
  }

  async publishPostViaBackend(postId: string): Promise<Post> {
    // Alternative method using backend's integrated publishing endpoint
    const response = await api.post(`/posts/${postId}/publish`);
    return response.data;
  }

  async schedulePost(postId: string, scheduledAt: string): Promise<Post> {
    const response = await api.post(`/posts/${postId}/schedule`, {
      scheduled_at: scheduledAt,
    });
    return response.data;
  }

  async schedulePostOrPostGroup(postGroupId: string, postId: string, scheduledAt: string): Promise<Post> {
    const response = await api.post(`/post-groups/${postGroupId}/schedule`, {
      post_id: postId,
      scheduled_at: scheduledAt,
    });
    return response.data;
  }

  async getPostGroups(page = 1, perPage = 20, status?: string, search?: string): Promise<PostGroupListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    if (status) {
      params.append('status', status);
    }
    if (search) {
      params.append('search', search);
    }
    const response = await api.get(`/post-groups/?${params}`);
    return response.data;
  }

  async getPostGroup(postGroupId: string): Promise<PostGroup> {
    const response = await api.get(`/post-groups/${postGroupId}`);
    return response.data;
  }

  async createPostGroup(data: CreatePostGroupData): Promise<PostGroup> {
    const response = await api.post('/post-groups/', data);
    return response.data;
  }

  async updatePostGroup(postGroupId: string, data: CreatePostGroupData): Promise<PostGroup> {
    const response = await api.put(`/post-groups/${postGroupId}`, data);
    return response.data;
  }

  async deletePostGroup(postGroupId: string): Promise<void> {
    await api.delete(`/post-groups/${postGroupId}`);
  }
}

export default new PostService();
